import React, { useMemo, useState } from "react";
import { AlertCircle, ArrowLeft, ArrowRight, Check, CheckCircle2, ChevronDown, Eye, FileCheck2, FilePlus2, FileText, Landmark, MapPin, Pencil, Plus, Save, ShieldCheck, Trash2, Upload, X } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Checkbox } from "../components/ui/Checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/Dialog";
import { Input } from "../components/ui/Input";
import { Progress } from "../components/ui/Progress";
import { Textarea } from "../components/ui/Textarea";
import { PublicShell, Screen } from "../components/common/GovShell";
import { InfoPanel, MapPlaceholder, StatusChip } from "../components/common/Ui";
type SaveState = "saving" | "saved";
type UploadState = "empty" | "uploading" | "ready" | "error";
type RecordKind = "deed" | "case" | "parcel" | "asset";
type UploadEntry = {
  name: string;
  state: UploadState;
  required: boolean;
  fileName?: string;
};
type FormField = {
  label: string;
  type?: "text" | "date" | "textarea" | "select" | "currency";
  required?: boolean;
  helper?: string;
  placeholder?: string;
  defaultValue?: string;
  options?: string[];
};
const STEPS = ["আবেদনকারীর তথ্য", "ওয়াক্‌ফ এস্টেট", "ওয়াক্‌ফ দলিল", "আয় ও ব্যয়", "মামলা", "সম্পত্তির রেকর্ড", "অন্যান্য সম্পদ", "দলিল সংযুক্তি", "ঘোষণা ও জমাদান"];
const APPLICANT_FIELDS: FormField[] = [{
  label: "আবেদনকারীর নাম",
  defaultValue: "মোঃ রফিকুল ইসলাম"
}, {
  label: "পিতার নাম",
  defaultValue: "মোঃ আবদুল হালিম"
}, {
  label: "মাতার নাম",
  defaultValue: "মোছাঃ রহিমা খাতুন"
}, {
  label: "আবেদনকারীর পরিচয় বা ভূমিকা",
  type: "select",
  options: ["মোতাওয়াল্লি", "ওয়াকিফের উত্তরাধিকারী", "ব্যবস্থাপনা কমিটির সদস্য", "অন্যান্য"]
}, {
  label: "ওয়াক্‌ফ এস্টেটের সঙ্গে সম্পর্ক",
  defaultValue: "বর্তমান মোতাওয়াল্লি"
}, {
  label: "জাতীয় পরিচয়পত্র নম্বর",
  helper: "NID নম্বর ১০ বা ১৭ অঙ্কের হতে হবে",
  defaultValue: "১৯৯০***********১২৩"
}, {
  label: "মোবাইল নম্বর",
  helper: "এই নম্বরেই সব বিজ্ঞপ্তি পাঠানো হবে",
  defaultValue: "০১********৩৪"
}, {
  label: "ই-মেইল, ঐচ্ছিক",
  required: false,
  placeholder: "example@email.com"
}];
const ADDRESS_FIELDS: FormField[] = [{
  label: "গ্রাম/মহল্লা/সড়ক",
  defaultValue: "বিরুলিয়া বাজার রোড"
}, {
  label: "ডাকঘর",
  defaultValue: "সাভার"
}, {
  label: "ইউনিয়ন/ওয়ার্ড",
  defaultValue: "বিরুলিয়া ইউনিয়ন"
}, {
  label: "উপজেলা/থানা",
  defaultValue: "সাভার"
}, {
  label: "জেলা",
  defaultValue: "ঢাকা"
}];
const ESTATE_FIELDS: FormField[] = [{
  label: "ওয়াক্‌ফ এস্টেটের প্রস্তাবিত নাম",
  defaultValue: "আল-আমিন জামে মসজিদ ওয়াক্‌ফ এস্টেট"
}, {
  label: "ওয়াক্‌ফের সংক্ষিপ্ত বিবরণ",
  type: "textarea",
  defaultValue: "স্থানীয় ধর্মীয় কার্যক্রম এবং দুঃস্থ মানুষের কল্যাণে প্রতিষ্ঠিত ওয়াক্‌ফ সম্পত্তি।"
}, {
  label: "ওয়াক্‌ফের উদ্দেশ্য",
  type: "textarea",
  defaultValue: "মসজিদ পরিচালনা, ধর্মীয় শিক্ষা, এতিম ও অসচ্ছল মানুষের সহায়তা।"
}, {
  label: "প্রধান উপকারভোগী বা জনকল্যাণমূলক উদ্দেশ্য",
  type: "textarea",
  defaultValue: "স্থানীয় মুসল্লি, মাদ্রাসার শিক্ষার্থী এবং অসচ্ছল পরিবার।"
}, {
  label: "এস্টেটের প্রধান ঠিকানা",
  type: "textarea",
  defaultValue: "বিরুলিয়া, সাভার, ঢাকা"
}, {
  label: "প্রধান সম্পত্তির বিভাগ",
  type: "select",
  options: ["ঢাকা", "চট্টগ্রাম", "রাজশাহী", "খুলনা", "সিলেট", "বরিশাল", "রংপুর", "ময়মনসিংহ"]
}, {
  label: "প্রধান সম্পত্তির জেলা",
  type: "select",
  options: ["ঢাকা", "গাজীপুর", "নারায়ণগঞ্জ", "ময়মনসিংহ"]
}, {
  label: "প্রধান সম্পত্তির উপজেলা/থানা",
  defaultValue: "সাভার"
}, {
  label: "বর্তমান মোতাওয়াল্লির নাম",
  defaultValue: "মোঃ রফিকুল ইসলাম"
}, {
  label: "প্রতিষ্ঠার আনুমানিক তারিখ",
  type: "date",
  defaultValue: "2011-06-15"
}];
const DEED_FIELDS: FormField[] = [{
  label: "ওয়াক্‌ফ দলিল নম্বর",
  defaultValue: "৪৫৮/২০১১"
}, {
  label: "দলিল রেজিস্ট্রির তারিখ",
  type: "date",
  defaultValue: "2011-06-15"
}, {
  label: "দাতা বা ওয়াকিফের নাম",
  defaultValue: "আলহাজ্ব আবদুল কাদের"
}, {
  label: "সাব-রেজিস্ট্রি অফিস",
  defaultValue: "সাভার সাব-রেজিস্ট্রি অফিস"
}, {
  label: "দলিলের ধরন",
  type: "select",
  options: ["নিবন্ধিত ওয়াক্‌ফ দলিল", "দানপত্র", "ঘোষণাপত্র", "অন্যান্য"]
}, {
  label: "দলিলের সংক্ষিপ্ত বিবরণ",
  type: "textarea"
}, {
  label: "মন্তব্য",
  type: "textarea",
  required: false
}];
const CASE_FIELDS: FormField[] = [{
  label: "মামলা-মোকদ্দমার নম্বর"
}, {
  label: "মামলা দায়েরের তারিখ",
  type: "date"
}, {
  label: "আদালতের নাম"
}, {
  label: "মামলার ধরন",
  type: "select",
  options: ["দেওয়ানি", "ফৌজদারি", "ভূমি বিরোধ", "অন্যান্য"]
}, {
  label: "বর্তমান অবস্থা",
  type: "select",
  options: ["বিচারাধীন", "নিষ্পত্তি হয়েছে", "স্থগিত", "আপিলাধীন"]
}, {
  label: "বাদী"
}, {
  label: "বিবাদী"
}, {
  label: "পরবর্তী শুনানির তারিখ",
  type: "date"
}, {
  label: "সংক্ষিপ্ত বিবরণ",
  type: "textarea"
}];
const LAND_FIELDS: FormField[] = [{
  label: "জে.এল. নম্বর"
}, {
  label: "মৌজার নাম"
}, {
  label: "সম্পত্তির শ্রেণি",
  type: "select",
  options: ["কৃষি", "বসতভিটা", "বাণিজ্যিক", "পুকুর", "অন্যান্য"]
}, {
  label: "সিএস খতিয়ান নম্বর",
  required: false
}, {
  label: "এসএ খতিয়ান নম্বর",
  required: false
}, {
  label: "আরএস খতিয়ান নম্বর"
}, {
  label: "সিএস দাগ নম্বর",
  required: false
}, {
  label: "এসএ দাগ নম্বর",
  required: false
}, {
  label: "আরএস দাগ নম্বর"
}, {
  label: "বিএস/সিটি খতিয়ান নম্বর",
  required: false
}, {
  label: "বিএস/হাল দাগ নম্বর",
  required: false
}, {
  label: "ওয়াক্‌ফকৃত জমির পরিমাণ"
}, {
  label: "পরিমাপের একক: একর/শতাংশ",
  type: "select",
  options: ["একর", "শতাংশ", "কাঠা", "বিঘা"]
}, {
  label: "বর্তমান ব্যবহার"
}, {
  label: "বর্তমান দখলের অবস্থা",
  type: "select",
  options: ["ওয়াক্‌ফের দখলে", "আংশিক দখলে", "বেদখল", "বিরোধপূর্ণ"]
}, {
  label: "আনুমানিক মূল্য, ঐচ্ছিক",
  type: "currency",
  required: false
}, {
  label: "মন্তব্য",
  type: "textarea",
  required: false
}];
const ASSET_FIELDS: FormField[] = [{
  label: "সম্পদের ধরন",
  type: "select",
  options: ["ভবন", "দোকান বা মার্কেট", "ব্যাংক হিসাব", "বিনিয়োগ", "যানবাহন", "অন্যান্য"]
}, {
  label: "সম্পদের নাম বা বিবরণ"
}, {
  label: "প্রতিষ্ঠান বা ব্যাংকের নাম",
  required: false
}, {
  label: "হিসাব বা রেফারেন্স নম্বর",
  required: false
}, {
  label: "অবস্থান"
}, {
  label: "পরিমাণ"
}, {
  label: "আনুমানিক মূল্য",
  type: "currency"
}, {
  label: "বার্ষিক আয়",
  type: "currency"
}, {
  label: "মালিকানা বা নিয়ন্ত্রণের অবস্থা"
}, {
  label: "মন্তব্য",
  type: "textarea",
  required: false
}];
const DOCUMENTS: UploadEntry[] = [{
  name: "আবেদনকারীর ছবি",
  required: true,
  state: "ready",
  fileName: "rafiqul-photo.jpg · 384 KB"
}, {
  name: "জাতীয় পরিচয়পত্র",
  required: true,
  state: "ready",
  fileName: "nid-masked.pdf · 1.1 MB"
}, {
  name: "ওয়াক্‌ফ দলিল",
  required: true,
  state: "ready",
  fileName: "waqf-deed-2011.pdf · 2.4 MB"
}, {
  name: "খতিয়ান/পর্চা",
  required: true,
  state: "empty"
}, {
  name: "দাগ ও মৌজা মানচিত্র",
  required: false,
  state: "empty"
}, {
  name: "নামজারি বা মিউটেশন দলিল",
  required: false,
  state: "empty"
}, {
  name: "ভূমি উন্নয়ন কর বা দাখিলা",
  required: false,
  state: "empty"
}, {
  name: "মামলার দলিল",
  required: false,
  state: "empty"
}, {
  name: "সম্পত্তির ছবি",
  required: false,
  state: "empty"
}, {
  name: "ব্যাংক বা আর্থিক সম্পদের প্রমাণ",
  required: false,
  state: "empty"
}, {
  name: "ব্যবস্থাপনা কমিটির কাগজপত্র",
  required: false,
  state: "empty"
}, {
  name: "অন্যান্য দলিল",
  required: false,
  state: "empty"
}];
export function ApplicationFlow({
  screen,
  setScreen
}: {
  screen: Screen;
  setScreen: (screen: Screen) => void;
}) {
  const [step, setStep] = useState(0);
  const [deeds, setDeeds] = useState([1]);
  const [cases, setCases] = useState<number[]>([]);
  const [parcels, setParcels] = useState([1]);
  const [assets, setAssets] = useState([1]);
  const [documents, setDocuments] = useState(DOCUMENTS);
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [recoverOpen, setRecoverOpen] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const completed = useMemo(() => new Set(Array.from({
    length: step
  }, (_, index) => index)), [step]);
  const progress = Math.round((step + 1) / STEPS.length * 100);
  function saveDraft() {
    setSaveState("saving");
    window.setTimeout(() => setSaveState("saved"), 700);
  }
  function moveTo(target: number) {
    saveDraft();
    setStep(target);
    setShowValidation(false);
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }
  function next() {
    if (step === 0 && !showValidation) {
      setShowValidation(true);
      return;
    }
    if (step === 8) {
      if (!declared) {
        setShowValidation(true);
        return;
      }
      setSubmitOpen(true);
      return;
    }
    moveTo(Math.min(step + 1, STEPS.length - 1));
  }
  function addRecord(kind: RecordKind) {
    const update: Record<RecordKind, React.Dispatch<React.SetStateAction<number[]>>> = {
      deed: setDeeds,
      case: setCases,
      parcel: setParcels,
      asset: setAssets
    };
    update[kind](items => [...items, Date.now()]);
  }
  function removeRecord(kind: RecordKind, id: number) {
    const update: Record<RecordKind, React.Dispatch<React.SetStateAction<number[]>>> = {
      deed: setDeeds,
      case: setCases,
      parcel: setParcels,
      asset: setAssets
    };
    update[kind](items => items.filter(item => item !== id));
  }
  function uploadDocument(index: number) {
    setDocuments(items => items.map((item, itemIndex) => itemIndex === index ? {
      ...item,
      state: "uploading"
    } : item));
    window.setTimeout(() => setDocuments(items => items.map((item, itemIndex) => itemIndex === index ? {
      ...item,
      state: "ready",
      fileName: `${item.name.replaceAll(" ", "-").toLowerCase()}.pdf · 1.8 MB`
    } : item)), 900);
  }
  return <PublicShell screen={screen} setScreen={setScreen} data-id="element-905" data-mp="1b1zenk8wug9">
    <main className="mx-auto max-w-[1240px] px-4 py-6 sm:py-8" data-id="element-906" data-mp="19h4gwu0r9hv">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500" data-id="element-907" data-mp="6a53o5seiwn6">
        <p data-id="element-908" data-mp="3nj1y0u1aimze">হোম <span className="mx-1" data-id="element-909" data-mp="3nj1uik2304un">/</span> অনলাইন সেবা <span className="mx-1" data-id="element-910" data-mp="3nkczgn79jinc">/</span> ওয়াক্‌ফ সম্পত্তি তালিকাভুক্তি</p>
        <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-sm" data-id="element-911" data-mp="3shli0owbpop4">
          <span className={`h-2 w-2 rounded-full ${saveState === "saving" ? "bg-amber-500 animate-pulse" : "bg-[#006a4e]"}`} data-id="element-912" data-mp="k21v07m8x2ii" />
          {saveState === "saving" ? "স্বয়ংক্রিয়ভাবে সংরক্ষণ হচ্ছে…" : "খসড়া সংরক্ষিত · ২ মিনিট আগে"}
        </span>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6" data-id="element-913" data-mp="fa1mhqs411ab">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-914" data-mp="12zm9qlvaqkpe">
          <div data-id="element-915" data-mp="17rw4cf7cw8y0">
            <p className="text-xs font-bold text-[#006a4e]" data-id="element-916" data-mp="17sfbr1zdrexs">ওয়াক্‌ফ অধ্যাদেশ, ১৯৬২-এর ৪৭ ধারা</p>
            <h1 className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl" data-id="element-917" data-mp="2sfotdwvi556w">ওয়াক্‌ফ সম্পত্তি তালিকাভুক্তির আবেদন</h1>
            <p className="mt-1 text-sm text-slate-600" data-id="element-918" data-mp="2ng2ihu35c119">ধাপ {step + 1} / {STEPS.length} · {STEPS[step]}</p>
          </div>
          <Button variant="outline" size="sm" onClick={saveDraft} data-id="element-919" data-mp="2x7zswymbyoyx"><Save className="mr-1.5 h-4 w-4" data-id="element-920" data-mp="2x8hzop6mcll3" />খসড়া সংরক্ষণ</Button>
        </div>

        <div className="mt-5 overflow-x-auto pb-1" data-id="element-921" data-mp="29jgmkomucm9h">
          <ol className="flex min-w-[900px] items-start" data-id="element-922" data-mp="29k8g7twvo9x4">
            {STEPS.map((label, index) => <li key={label} className="relative flex flex-1 flex-col items-center text-center" data-id="element-923" data-mp="24lf5g98z6x0n" data-mpi={index}>
              <button onClick={() => index <= step && moveTo(index)} aria-label={`${label} ধাপে যান`} className={`z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors ${completed.has(index) ? "border-[#006a4e] bg-[#006a4e] text-white" : index === step ? "border-[#006a4e] bg-white text-[#006a4e]" : "border-slate-300 bg-white text-slate-500"}`} data-id="element-924" data-mp="2igz9jyk1pao0">
                {completed.has(index) ? <Check className="h-4 w-4" data-id="element-925" data-mp="2rkuflvnefsbt" /> : index + 1}
              </button>
              {index < STEPS.length - 1 && <span className={`absolute left-[53%] top-4 h-0.5 w-[94%] ${completed.has(index) ? "bg-[#006a4e]" : "bg-slate-200"}`} data-id="element-926" data-mp="2wk74elxtz9h1" />}
              <span className={`mt-2 max-w-[92px] text-[11px] leading-4 ${index === step ? "font-bold text-[#006a4e]" : "text-slate-500"}`} data-id="element-927" data-mp="30k7skyaqez1a">{label}</span>
            </li>)}
          </ol>
        </div>
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_288px]" data-id="element-928" data-mp="28uuypw1ppjjh">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7" data-id="element-929" data-mp="2cuvk60n8av01">
          {step === 0 && <ApplicantStep showValidation={showValidation} data-id="element-930" data-mp="2hng9aov6wjx8" />}
          {step === 1 && <EstateStep data-id="element-931" data-mp="1l5k1gojkm7a3" />}
          {step === 2 && <RepeatableRecords kind="deed" title="ওয়াক্‌ফ দলিলের বিবরণ" description="প্রতিটি দলিল আলাদাভাবে যোগ করুন। একাধিক দলিল থাকলে ‘আরেকটি দলিল যোগ করুন’ বাটন ব্যবহার করুন।" records={deeds} fields={DEED_FIELDS} addLabel="আরেকটি দলিল যোগ করুন" attachment="দলিলের স্ক্যান কপি" onAdd={addRecord} onRemove={removeRecord} data-id="element-932" data-mp="1l4ifvq4xi2e4" />}
          {step === 3 && <FinanceStep data-id="element-933" data-mp="18lwktfhaf2o2" />}
          {step === 4 && <CaseStep records={cases} onAdd={addRecord} onRemove={removeRecord} data-id="element-934" data-mp="18ktr6zskym4g" />}
          {step === 5 && <LandStep records={parcels} onAdd={addRecord} onRemove={removeRecord} data-id="element-935" data-mp="13ma4uya2r8bk" />}
          {step === 6 && <RepeatableRecords kind="asset" title="অন্যান্য স্থাবর ও অস্থাবর সম্পদ" description="ভবন, দোকান, ব্যাংক হিসাব, বিনিয়োগ বা অন্যান্য সম্পদের তথ্য যোগ করুন। ব্যাংক ও রেফারেন্স নম্বর কেবল প্রশাসনিক যাচাইয়ের জন্য ব্যবহৃত হবে।" records={assets} fields={ASSET_FIELDS} addLabel="আরেকটি সম্পদ যোগ করুন" attachment="সমর্থনকারী দলিল" onAdd={addRecord} onRemove={removeRecord} data-id="element-936" data-mp="yoqtq78obbnu" />}
          {step === 7 && <DocumentsStep documents={documents} onUpload={uploadDocument} data-id="element-937" data-mp="fwfv4pl1xzvj" />}
          {step === 8 && <ReviewStep declared={declared} setDeclared={setDeclared} showValidation={showValidation} onEdit={moveTo} onPreview={() => setPreviewOpen(true)} data-id="element-938" data-mp="ay4nmli8c1gh" />}
        </section>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start" data-id="element-939" data-mp="2fzh0po1l404r">
          <InfoPanel tone="blue" data-id="element-940" data-mp="2g1tju4j2wgje"><b data-id="element-941" data-mp="21b42ekf0y573">ধাপে ধাপে পূরণ করুন</b><br data-id="element-942" data-mp="21bm59zw6zhnz" />আবশ্যক ঘরগুলো <span className="font-bold text-[#f42a41]" data-id="element-943" data-mp="21a1ac95ytwpl">*</span> দিয়ে চিহ্নিত। তথ্য অসম্পূর্ণ থাকলেও খসড়া সংরক্ষণ করে পরে ফিরতে পারবেন।</InfoPanel>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-944" data-mp="1sa5wfwl0znlv">
            <div className="flex items-center justify-between" data-id="element-945" data-mp="1scio51gl5cc4"><p className="text-sm font-bold" data-id="element-946" data-mp="1wcssk9oqbuov">আবেদন অগ্রগতি</p><b className="text-sm text-[#006a4e]" data-id="element-947" data-mp="1w9eql2jz9nhy">{progress}%</b></div>
            <Progress className="mt-3" value={progress} data-id="element-948" data-mp="1idjepgnzxco2" />
            <p className="mt-2 text-xs leading-5 text-slate-500" data-id="element-949" data-mp="1najifkxdi3c0">{step + 1}টি ধাপ দেখা হচ্ছে, মোট {STEPS.length}টি ধাপ। পূরণ করা অংশগুলো পরে সম্পাদনা করা যাবে।</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-950" data-id="element-950" data-mp="3hp2lpppqoav3">
            <p className="flex items-center gap-1.5 font-bold" data-id="element-951" data-mp="161h8rmu6dby0"><div className="h-4 w-4" data-id="element-952" data-mp="1628so0eetl8g" /> সহায়তা</p>
            <p className="mt-1" data-id="element-953" data-mp="112w6lhvd4i6x">দলিলের তথ্য স্পষ্ট না হলে স্ক্যানের বদলে পরিষ্কার ছবি যুক্ত করুন। NID, SMS, পেমেন্ট ও ই-স্বাক্ষর যাচাই এই প্রোটোটাইপে সিমুলেটেড।</p>
          </div>
        </aside>
      </div>

      <div className="sticky bottom-0 z-20 mt-6 flex flex-col-reverse justify-between gap-3 border-t border-slate-200 bg-[#f6f8f7]/95 py-4 backdrop-blur sm:flex-row" data-id="element-954" data-mp="1b0acinjntokz">
        <Button variant="outline" onClick={() => moveTo(Math.max(0, step - 1))} disabled={step === 0} data-id="element-955" data-mp="1ou1qc84fbumi"><ArrowLeft className="mr-2 h-4 w-4" data-id="element-956" data-mp="1jv8cufp503mc" />আগের ধাপ</Button>
        <div className="flex flex-wrap gap-2" data-id="element-957" data-mp="1yp3eb0myqqni">
          <Button variant="ghost" onClick={() => {
            saveDraft();
            setScreen("citizen");
          }} data-id="element-958" data-mp="1trbn68u578kd">সংরক্ষণ করে পরে আসুন</Button>
          <Button className="bg-[#006a4e] hover:bg-[#00543f]" onClick={next} data-id="element-959" data-mp="4d6iywleh0n6">{step === STEPS.length - 1 ? "আবেদন জমা দিন" : "পরবর্তী ধাপ"}<ArrowRight className="ml-2 h-4 w-4" data-id="element-960" data-mp="3vkrel7y20rge" /></Button>
        </div>
      </div>
    </main>

    <Dialog open={recoverOpen} onOpenChange={setRecoverOpen} data-id="element-961" data-mp="p38zz6tpx3rt">
      <DialogContent data-id="element-962" data-mp="t42oo2ezimlr">
        <DialogHeader data-id="element-963" data-mp="t3jghdf4zckx"><DialogTitle data-id="element-964" data-mp="t3b19evjjv6k">সংরক্ষিত খসড়া পাওয়া গেছে</DialogTitle><DialogDescription data-id="element-965" data-mp="y1kxlst051tr">২০ জুলাই ২০২৬, ০৯:৩০-এ এই আবেদনটি সংরক্ষিত হয়েছিল। আপনি যেখানে থেমেছিলেন সেখান থেকে কাজ শুরু করতে পারেন।</DialogDescription></DialogHeader>
        <div className="rounded-xl border border-[#006a4e]/20 bg-[#f5faf7] p-4 text-sm" data-id="element-966" data-mp="12yuo7bs4njj6"><b data-id="element-967" data-mp="1801wdby6mkqd">খসড়া: আল-আমিন জামে মসজিদ ওয়াক্‌ফ এস্টেট</b><p className="mt-1 text-slate-600" data-id="element-968" data-mp="1c1dmdikyx7z3">২টি ধাপ পূরণ করা হয়েছে · দলিলের তথ্য অসম্পূর্ণ</p></div>
        <DialogFooter data-id="element-969" data-mp="1gs3siy17xeuu"><Button variant="outline" onClick={() => {
            setRecoverOpen(false);
            moveTo(0);
          }} data-id="element-970" data-mp="1gsc66s560o73">নতুন করে শুরু</Button><Button className="bg-[#006a4e]" onClick={() => {
            setRecoverOpen(false);
            moveTo(2);
          }} data-id="element-971" data-mp="1lqx60qgi95v0">খসড়া পুনরুদ্ধার করুন</Button></DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={previewOpen} onOpenChange={setPreviewOpen} data-id="element-972" data-mp="1vns4588v948r">
      <DialogContent data-id="element-973" data-mp="3g8yjajmnf8w9">
        <DialogHeader data-id="element-974" data-mp="3g9i0tz8dzv9r"><DialogTitle data-id="element-975" data-mp="3g5u832arncw3">আবেদনের প্রাকদর্শন</DialogTitle><DialogDescription data-id="element-976" data-mp="3b8ave6tpv869">জমা দেওয়ার আগে সংক্ষিপ্তসারটি যাচাই করুন। সম্পূর্ণ আবেদন কপি জমার পর ডাউনলোড করা যাবে।</DialogDescription></DialogHeader>
        <div className="max-h-[55vh] space-y-3 overflow-y-auto rounded-xl border bg-slate-50 p-4 text-sm" data-id="element-977" data-mp="369zl5uzievni">
          <PreviewRow label="আবেদনকারী" value="মোঃ রফিকুল ইসলাম · মোতাওয়াল্লি" data-id="element-978" data-mp="31cr3rwm6qvnb" />
          <PreviewRow label="প্রস্তাবিত এস্টেট" value="আল-আমিন জামে মসজিদ ওয়াক্‌ফ এস্টেট" data-id="element-979" data-mp="2shgtxiy46ru" />
          <PreviewRow label="প্রধান সম্পত্তি" value="বিরুলিয়া, সাভার, ঢাকা" data-id="element-980" data-mp="3tztxmbfx2i78" />
          <PreviewRow label="ওয়াক্‌ফ দলিল" value={`${deeds.length}টি রেকর্ড`} data-id="element-981" data-mp="3p7tuka4jv96f" />
          <PreviewRow label="জমির রেকর্ড" value={`${parcels.length}টি পার্সেল`} data-id="element-982" data-mp="3ka20b9gfnaz2" />
          <PreviewRow label="সংযুক্ত দলিল" value={`${documents.filter(item => item.state === "ready").length}টি যুক্ত`} data-id="element-983" data-mp="ljiac61ara40" />
        </div>
        <DialogFooter data-id="element-984" data-mp="gsjvzbzvcm3e"><Button onClick={() => setPreviewOpen(false)} data-id="element-985" data-mp="gs0ajlb0ffkm">প্রাকদর্শন বন্ধ করুন</Button></DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={submitOpen} onOpenChange={setSubmitOpen} data-id="element-986" data-mp="3l8icbew4jxvw">
      <DialogContent data-id="element-987" data-mp="3q2d1gxui152d">
        <DialogHeader data-id="element-988" data-mp="3q2mivx1ha3wg"><DialogTitle data-id="element-989" data-mp="3q3fv3dj7hllz">আবেদন জমা দিতে প্রস্তুত?</DialogTitle><DialogDescription data-id="element-990" data-mp="3capx0aqekq8i">জমা দেওয়ার পর আবেদনটি ঢাকা জেলা ওয়াক্‌ফ কার্যালয়ে প্রাথমিক যাচাইয়ের জন্য প্রেরিত হবে।</DialogDescription></DialogHeader>
        <div className="space-y-2 rounded-xl bg-[#f5faf7] p-4 text-sm" data-id="element-991" data-mp="3h6x4i7yg69sk"><p data-id="element-992" data-mp="8u996x9mjg1y"><b data-id="element-993" data-mp="8uj3gx1pc9dg">প্রধান জেলা:</b> ঢাকা</p><p data-id="element-994" data-mp="8tznhlvm3v22"><b data-id="element-995" data-mp="8r3lnmgn8m8c">ওয়াক্‌ফ এস্টেট:</b> আল-আমিন জামে মসজিদ ওয়াক্‌ফ এস্টেট</p><p data-id="element-996" data-mp="ctrw1yl0a17f"><b data-id="element-997" data-mp="ctry044kgba2">সংযুক্ত দলিল:</b> {documents.filter(item => item.state === "ready").length}টি</p></div>
        <div className="flex items-start gap-2 text-xs text-slate-500" data-id="element-998" data-mp="3uzd04np0r5jg"><ShieldCheck className="mt-0.5 h-4 w-4 text-[#006a4e]" data-id="element-999" data-mp="3u4sp78pulbq" />জমাদানের সময়, ব্যবহারকারী এবং দেওয়া ঘোষণা নিরাপদ অডিট রেকর্ডে সংরক্ষিত হবে।</div>
        <DialogFooter data-id="element-1000" data-mp="2jkhnf79xne5j"><Button variant="outline" onClick={() => setSubmitOpen(false)} data-id="element-1001" data-mp="2jjoebzni4ck8">ফিরে যাচাই করুন</Button><Button className="bg-[#006a4e]" onClick={() => {
            setSubmitOpen(false);
            setScreen("receipt");
          }} data-id="element-1002" data-mp="2ol59rfn7v6tf">নিশ্চিত করে জমা দিন</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </PublicShell>;
}
function ApplicantStep({
  showValidation
}: {
  showValidation: boolean;
}) {
  return <div data-id="element-1003" data-mp="2q0q4ryya92sh">
    <StepHeading title="আবেদনকারীর তথ্য" description="নিজের পরিচয় ও যোগাযোগের তথ্য দিন। এই তথ্য কেবল আবেদন যাচাই ও যোগাযোগের জন্য ব্যবহার করা হবে।" data-id="element-1004" data-mp="2pzf0ly0xdt0u" />
    {showValidation && <ValidationNotice message="আবেদনকারীর পরিচয় বা ভূমিকা এবং বর্তমান ঠিকানার কয়েকটি আবশ্যক তথ্য পূরণ করুন।" data-id="element-1005" data-mp="1xdnc3ngafma5" />}
    <SectionCard title="পরিচয় ও ভূমিকা" description="আবেদনকারী মোতাওয়াল্লি না হলে এস্টেটের সঙ্গে সম্পর্কটি স্পষ্ট করুন।" data-id="element-1006" data-mp="1tctj4fnznhae"><div className="grid gap-5 sm:grid-cols-2" data-id="element-1007" data-mp="274qc0lxhdv7u">{APPLICANT_FIELDS.map(field => <FormFieldView key={field.label} field={field} error={showValidation && field.label === "আবেদনকারীর পরিচয় বা ভূমিকা"} data-id="element-1008" data-mp="22b5c2ocbur60" />)}</div></SectionCard>
    <SectionCard title="বর্তমান ঠিকানা" description="স্থায়ী ঠিকানা ভিন্ন হলে পরবর্তী যাচাইয়ে আলাদা তথ্য চাওয়া হতে পারে।" className="mt-5" data-id="element-1009" data-mp="19n9elhexov86">
      <label className="block text-sm font-bold text-slate-700" data-id="element-1010" data-mp="644u233bty3y">বর্তমান ও স্থায়ী ঠিকানা একই কি না?<span className="text-[#f42a41]" data-id="element-1011" data-mp="14rzsxa4lorc"> *</span><select className="mt-2 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm" data-id="element-1012" data-mp="11xd0z3n3rur"><option data-id="element-1013" data-mp="fu7ng4ed1e2t">হ্যাঁ, একই</option><option data-id="element-1014" data-mp="b26cqmwubvew">না, ভিন্ন</option></select></label>
      <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1015" data-mp="b1ehjd75nzp5">{ADDRESS_FIELDS.map(field => <FormFieldView key={field.label} field={field} error={showValidation && field.label === "গ্রাম/মহল্লা/সড়ক"} data-id="element-1016" data-mp="ot1ms2j2sik2" />)}<UploadTile label="আবেদনকারীর ছবি" helper="পরিষ্কার সাম্প্রতিক ছবি (JPG/PNG, সর্বোচ্চ ২ MB)" initial="rafiqul-photo.jpg · 384 KB" data-id="element-1017" data-mp="kqy0sjys5vkj" /></div>
    </SectionCard>
  </div>;
}
function EstateStep() {
  return <div data-id="element-1018" data-mp="ttpgwx2lpfii"><StepHeading title="ওয়াক্‌ফ এস্টেটের পরিচিতি" description="এস্টেটের নাম, উদ্দেশ্য ও প্রধান সম্পত্তির অবস্থান দিন।" data-id="element-1019" data-mp="ttpcmkvkitcp" />
    <InfoPanel tone="amber" data-id="element-1020" data-mp="30jdsxp3hbjt3"><b data-id="element-1021" data-mp="30kymbbe24wpd">অফিস রাউটিং</b><br data-id="element-1022" data-mp="2vkakrvs7cxgw" />প্রধান সম্পত্তির জেলা অনুযায়ী গ্রহণকারী জেলা ওয়াক্‌ফ কার্যালয় নির্ধারিত হবে। অন্য জেলায় সম্পত্তি থাকলে সম্পত্তি রেকর্ড ধাপে আলাদা করে যোগ করুন।</InfoPanel>
    <SectionCard title="এস্টেটের মৌলিক পরিচিতি" description="নামের বানান দলিলের সঙ্গে মিলিয়ে লিখুন।" className="mt-5" data-id="element-1023" data-mp="1b2rwtcwe734y"><div className="grid gap-5 sm:grid-cols-2" data-id="element-1024" data-mp="1g2xlck7d89it">{ESTATE_FIELDS.map(field => <FormFieldView key={field.label} field={field} wide={field.type === "textarea"} data-id="element-1025" data-mp="11bnk6ju6vuda" />)}</div></SectionCard>
    <div className="mt-5 rounded-xl border border-[#006a4e]/20 bg-[#f5faf7] p-4 text-sm" data-id="element-1026" data-mp="169grjg78auf5"><MapPin className="mr-1 inline h-4 w-4 text-[#006a4e]" data-id="element-1027" data-mp="sapc9qgmr2pf" /><b data-id="element-1028" data-mp="xabukebbf953">প্রাথমিক রাউটিং:</b> ঢাকা জেলা ওয়াক্‌ফ কার্যালয় · সাভার উপজেলা</div>
  </div>;
}
function FinanceStep() {
  const income = ["ওয়াক্‌ফ সম্পত্তির মোট বার্ষিক আয়", "ভাড়া থেকে আয়", "কৃষি বা ফসল থেকে আয়", "ব্যবসা বা বিনিয়োগ থেকে আয়", "অনুদান", "অন্যান্য আয়"];
  const expense = ["মোট বার্ষিক ব্যয়", "সম্পত্তি রক্ষণাবেক্ষণ", "সংরক্ষণ ও মেরামত", "ধর্মীয় বা জনকল্যাণমূলক ব্যয়", "কর্মচারী ব্যয়", "আইনগত ব্যয়", "ইউটিলিটি ও প্রশাসনিক ব্যয়", "অন্যান্য ব্যয়"];
  return <div data-id="element-1029" data-mp="2hpnhdn1nh5wu"><StepHeading title="বার্ষিক আয় ও ব্যয়" description="সর্বশেষ পূর্ণ অর্থবছরের আনুমানিক তথ্য দিন। অঙ্কগুলো বাংলা বা ইংরেজি সংখ্যায় লেখা যাবে।" data-id="element-1030" data-mp="2hpng7jpwyzv9" />
    <div className="grid gap-5 xl:grid-cols-2" data-id="element-1031" data-mp="2b9nbqjxosgji"><SectionCard title="আয়ের বিবরণ" description="সব উৎস মিলিয়ে বার্ষিক মোট আয় লিখুন।" data-id="element-1032" data-mp="2b8lrppyp0jpn"> <div className="space-y-4" data-id="element-1033" data-mp="26bv8itrld800">{income.map((label, index) => <MoneyField key={label} label={label} defaultValue={index === 0 ? "৪৮৫০০০" : ""} data-id="element-1034" data-mp="229i7ujrpc0a3" data-mpi={index} />)}<label className="block text-sm font-bold" data-id="element-1035" data-mp="22buxlj3pbexp">আয়ের বিবরণ<Textarea className="mt-2" placeholder="ভাড়া, কৃষি বা অন্য আয়ের উৎস সংক্ষেপে লিখুন" data-id="element-1036" data-mp="2yyprtncoa1bw" /></label></div></SectionCard>
      <SectionCard title="ব্যয়ের বিবরণ" description="ওয়াক্‌ফের উদ্দেশ্য পূরণে হওয়া ব্যয় অন্তর্ভুক্ত করুন।" data-id="element-1037" data-mp="2u17p4ep90icc"><div className="space-y-4" data-id="element-1038" data-mp="2p8ejzppvb7zq">{expense.map((label, index) => <MoneyField key={label} label={label} defaultValue={index === 0 ? "২৪০০০০" : ""} data-id="element-1039" data-mp="2l8w458cwln3r" data-mpi={index} />)}<label className="block text-sm font-bold" data-id="element-1040" data-mp="3hq0gdxuxe20m">ব্যয়ের বিবরণ<Textarea className="mt-2" placeholder="রক্ষণাবেক্ষণ ও জনকল্যাণমূলক ব্যয়ের তথ্য লিখুন" data-id="element-1041" data-mp="3hrtn1d5jgm8m" /></label></div></SectionCard></div>
    <div className="mt-5 grid gap-3 sm:grid-cols-3" data-id="element-1042" data-mp="3cxpe4ocpq44w"><SummaryBox label="মোট আয়" value="৳ ৪,৮৫,০০০" data-id="element-1043" data-mp="zhxtm49mx33h" /><SummaryBox label="মোট ব্যয়" value="৳ ২,৪০,০০০" data-id="element-1044" data-mp="zj8vfyjis0ry" /><SummaryBox label="উদ্বৃত্ত" value="৳ ২,৪৫,০০০" tone="green" data-id="element-1045" data-mp="14h0m6p8fhguk" /></div>
  </div>;
}
function CaseStep({
  records,
  onAdd,
  onRemove
}: {
  records: number[];
  onAdd: (kind: RecordKind) => void;
  onRemove: (kind: RecordKind, id: number) => void;
}) {
  const [none, setNone] = useState(records.length === 0);
  return <div data-id="element-1046" data-mp="1if8rbkfmbogt"><StepHeading title="মামলা-মোকদ্দমার বিবরণ" description="প্রযোজ্য মামলা থাকলে প্রতিটির তথ্য ও সংশ্লিষ্ট নথি যোগ করুন।" data-id="element-1047" data-mp="1if8olco8had4" />
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm font-bold" data-id="element-1048" data-mp="1ncicuozvzfzd"><Checkbox checked={none} onCheckedChange={checked => setNone(Boolean(checked))} data-id="element-1049" data-mp="1w6x1hi6b42v5" /> <span data-id="element-1050" data-mp="2169nk0pct5wo">বর্তমানে কোনো মামলা-মোকদ্দমা নেই <span className="mt-1 block text-xs font-normal text-slate-500" data-id="element-1051" data-mp="2171akn4vxvbc">মামলা না থাকলে এই নির্বাচন রাখুন; পরবর্তী সময়ে তথ্য পরিবর্তন করা যাবে।</span></span></label>
    {!none && <div className="mt-5" data-id="element-1052" data-mp="1siceod4nlrcu"><RepeatableRecords kind="case" title="মামলা-মোকদ্দমার তথ্য" description="প্রতিটি মামলা আলাদা করে যোগ করুন।" records={records} fields={CASE_FIELDS} addLabel="মামলার তথ্য যোগ করুন" attachment="সংশ্লিষ্ট দলিল সংযুক্তি" onAdd={onAdd} onRemove={onRemove} data-id="element-1053" data-mp="1shua8t7u86tu" /></div>}
    {none && <InfoPanel tone="green" data-id="element-1054" data-mp="1x9c9ncu7pzcp"><b data-id="element-1055" data-mp="1x8sv863rtt3f">কোনো মামলা উল্লেখ নেই</b><br data-id="element-1056" data-mp="1xand1bjqx53a" />আপনার ঘোষণার ভিত্তিতে আবেদনটি মামলা-মুক্ত হিসেবে সংরক্ষিত হবে।</InfoPanel>}
  </div>;
}
function LandStep({
  records,
  onAdd,
  onRemove
}: {
  records: number[];
  onAdd: (kind: RecordKind) => void;
  onRemove: (kind: RecordKind, id: number) => void;
}) {
  return <div data-id="element-1057" data-mp="1ji7jd1cqijf1"><StepHeading title="ওয়াক্‌ফ সম্পত্তির রেকর্ড" description="প্রতিটি জমির রেকর্ড বা পার্সেল আলাদা করে পূরণ করুন; অন্য জেলায় সম্পত্তি থাকলেও এখানেই যোগ করুন।" data-id="element-1058" data-mp="1ji7gmtlco5bc" />
    <SectionCard title="অবস্থান নির্ধারণ" description="প্রথম পার্সেলের জন্য প্রযোজ্য প্রশাসনিক এলাকা বাছাই করুন।" data-id="element-1059" data-mp="1el790e8uqui4"><div className="grid gap-4 sm:grid-cols-2" data-id="element-1060" data-mp="2pxzytji318vj"><FormFieldView field={{
          label: "জেলা",
          type: "select",
          options: ["ঢাকা", "ময়মনসিংহ", "গাজীপুর"]
        }} data-id="element-1061" data-mp="2px840awb7f6b" /><FormFieldView field={{
          label: "উপজেলা/থানা",
          defaultValue: "সাভার"
        }} data-id="element-1062" data-mp="2xjvhphx1hx5g" /><FormFieldView field={{
          label: "ইউনিয়ন",
          defaultValue: "বিরুলিয়া"
        }} data-id="element-1063" data-mp="32e9630dab40m" /><FormFieldView field={{
          label: "মৌজা",
          defaultValue: "বিরুলিয়া"
        }} data-id="element-1064" data-mp="2oj72wkztnsri" /></div></SectionCard>
    <div className="mt-5" data-id="element-1065" data-mp="3g8a8c43g395q"><RepeatableRecords kind="parcel" title="জমির রেকর্ড" description="দাগ, খতিয়ান এবং দখলের অবস্থা যতটা সম্ভব দলিল অনুযায়ী লিখুন।" records={records} fields={LAND_FIELDS} addLabel="আরেকটি জমির রেকর্ড যোগ করুন" attachment="খতিয়ান বা মানচিত্র" onAdd={onAdd} onRemove={onRemove} data-id="element-1066" data-mp="3g7qq0m9vuir6" /></div>
    <div className="mt-5" data-id="element-1067" data-mp="3bh1vey2wp5nt"><MapPlaceholder compact data-id="element-1068" data-mp="3bg8n3soau83k" /></div>
  </div>;
}
function DocumentsStep({
  documents,
  onUpload
}: {
  documents: UploadEntry[];
  onUpload: (index: number) => void;
}) {
  return <div data-id="element-1069" data-mp="7rypcavuyn8b"><StepHeading title="প্রয়োজনীয় দলিল সংযুক্তি" description="PDF, JPG বা PNG ফরম্যাটে ফাইল যুক্ত করুন। প্রতিটি ফাইল সর্বোচ্চ ১০ MB; পাঠযোগ্য কপি ব্যবহার করুন।" data-id="element-1070" data-mp="7sqk5jhmsgxj" />
    <div className="mb-5 rounded-xl border border-[#006a4e]/20 bg-[#f5faf7] p-4 text-sm" data-id="element-1071" data-mp="hm31nu1pllk5"><FileCheck2 className="mr-1 inline h-4 w-4 text-[#006a4e]" data-id="element-1072" data-mp="dndh9hi973r0" /><b data-id="element-1073" data-mp="8mprt09oauyw">{documents.filter(item => item.state === "ready").length}টি ফাইল যুক্ত হয়েছে</b> · প্রয়োজনীয় দলিলগুলোর অবস্থা জমার আগে যাচাই করুন।</div>
    <div className="space-y-3" data-id="element-1074" data-mp="3o62qqzs932b">{documents.map((document, index) => <DocumentRow key={document.name} document={document} onUpload={() => onUpload(index)} data-id="element-1075" data-mp="3uvr1vcbnbc0a" data-mpi={index} />)}</div>
  </div>;
}
function ReviewStep({
  declared,
  setDeclared,
  showValidation,
  onEdit,
  onPreview
}: {
  declared: boolean;
  setDeclared: (checked: boolean) => void;
  showValidation: boolean;
  onEdit: (step: number) => void;
  onPreview: () => void;
}) {
  const sections = ["আবেদনকারীর তথ্য", "ওয়াক্‌ফ এস্টেটের পরিচিতি", "ওয়াক্‌ফ দলিল", "বার্ষিক আয় ও ব্যয়", "মামলা-মোকদ্দমা", "সম্পত্তির রেকর্ড", "অন্যান্য সম্পদ", "দলিল সংযুক্তি"];
  return <div data-id="element-1076" data-mp="3777rmr91cp5k"><StepHeading title="ঘোষণা, পর্যালোচনা ও জমাদান" description="তথ্য যাচাই করে ঘোষণা দিন। যে কোনো অংশে ফিরে গিয়ে সম্পাদনা করতে পারবেন।" data-id="element-1077" data-mp="377h8npc3rlz4" />
    <div className="space-y-2" data-id="element-1078" data-mp="31zhuksyqwhqv">{sections.map((label, index) => <button key={label} onClick={() => onEdit(index)} className="flex w-full items-center justify-between rounded-xl border border-slate-200 p-4 text-left hover:border-[#006a4e]/40 hover:bg-[#f5faf7]" data-id="element-1079" data-mp="32240yp9wgr7i" data-mpi={index}><span data-id="element-1080" data-mp="3bwe2oxkqf1uw"><b className="block text-sm" data-id="element-1081" data-mp="3bw4f1gn5t6sd">{label}</b><span className="mt-1 block text-xs text-slate-500" data-id="element-1082" data-mp="2ebhswxzwb6xi">তথ্য সংরক্ষিত হয়েছে · সম্পাদনা করতে চাপুন</span></span><span className="inline-flex items-center gap-1 text-sm font-bold text-[#006a4e]" data-id="element-1083" data-mp="2jauqodsb1yev"><Pencil className="h-4 w-4" data-id="element-1084" data-mp="2o8cqneoch3aq" />সম্পাদনা</span></button>)}</div>
    <div className="mt-6 rounded-2xl border border-[#006a4e]/20 bg-[#f5faf7] p-5" data-id="element-1085" data-mp="2o6tcbtm912bl">
      <h3 className="font-bold" data-id="element-1086" data-mp="2t0xiianox6bm">আবেদনকারীর ঘোষণা</h3>
      <p className="mt-3 text-sm leading-7 text-slate-700" data-id="element-1087" data-mp="1vkqkbynqfkk6">আমি ঘোষণা করছি যে, এই আবেদনে প্রদত্ত তথ্য ও সংযুক্ত দলিল আমার জানা মতে সঠিক। প্রয়োজন হলে সংশ্লিষ্ট কার্যালয় কর্তৃক তথ্য ও দলিল যাচাইয়ের জন্য আমি সম্মতি প্রদান করছি।</p>
      <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm font-medium" data-id="element-1088" data-mp="3eqjold2lqrv0"><Checkbox checked={declared} onCheckedChange={checked => setDeclared(Boolean(checked))} data-id="element-1089" data-mp="3aoychtxw6hlv" /> <span data-id="element-1090" data-mp="3oohdm2sacsoo">তথ্যের সঠিকতা নিশ্চিত করছি এবং সরকারি যাচাইয়ে সম্মতি দিচ্ছি।</span></label>
      {showValidation && !declared && <p className="mt-3 flex items-center gap-1.5 text-xs font-bold text-red-700" data-id="element-1091" data-mp="3jntpbovfypy5"><AlertCircle className="h-4 w-4" data-id="element-1092" data-mp="1iqahcfoazkj" />জমা দেওয়ার আগে ঘোষণায় সম্মতি দেওয়া আবশ্যক।</p>}
      <div className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-1093" data-mp="3tnanzmca03km"><FormFieldView field={{
          label: "আবেদনকারীর নাম",
          defaultValue: "মোঃ রফিকুল ইসলাম"
        }} data-id="element-1094" data-mp="3tmj59c3s20br" /><FormFieldView field={{
          label: "তারিখ",
          type: "date",
          defaultValue: "2026-07-21"
        }} data-id="element-1095" data-mp="b89zkztzbpnu" /><div className="rounded-lg border border-dashed border-[#006a4e]/40 bg-white p-3 text-sm" data-id="element-1096" data-mp="l78w5k1gzuuh"><b data-id="element-1097" data-mp="l4v1hcapckng">সিমুলেটেড ই-স্বাক্ষর</b><br data-id="element-1098" data-mp="g8ebecjxzw7q" /><span className="text-xs text-slate-500" data-id="element-1099" data-mp="g8o5ab83ynip">বাস্তব সেবায় ডিজিটাল স্বাক্ষর/OTP যুক্ত হবে</span></div></div>
      <p className="mt-4 text-xs text-slate-500" data-id="element-1100" data-mp="ztof0owkhd7u">সংযুক্ত পৃষ্ঠা/দলিল: ১২টি · জমা দেওয়ার পরে আবেদন নম্বর ও রসিদ তৈরি হবে।</p>
    </div>
    <Button variant="outline" className="mt-5" onClick={onPreview} data-id="element-1101" data-mp="13uzx89d42w60"><Eye className="mr-2 h-4 w-4" data-id="element-1102" data-mp="pwjpqg7fd1mt" />আবেদনটি প্রাকদর্শন করুন</Button>
  </div>;
}
function RepeatableRecords({
  kind,
  title,
  description,
  records,
  fields,
  addLabel,
  attachment,
  onAdd,
  onRemove
}: {
  kind: RecordKind;
  title: string;
  description: string;
  records: number[];
  fields: FormField[];
  addLabel: string;
  attachment: string;
  onAdd: (kind: RecordKind) => void;
  onRemove: (kind: RecordKind, id: number) => void;
}) {
  return <div data-id="element-1103" data-mp="73frsin6c2me"><StepHeading title={title} description={description} data-id="element-1104" data-mp="73fsylywu8nz" />
    {records.map((record, index) => <section key={record} className="mb-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5" data-id="element-1105" data-mp="c29447qq5nkz" data-mpi={index}><div className="mb-5 flex items-start justify-between gap-3" data-id="element-1106" data-mp="3tejyhxkt1b1h"><div data-id="element-1107" data-mp="24uqsh03leww"><p className="text-sm font-bold text-[#006a4e]" data-id="element-1108" data-mp="24usqmjnrozj">{title.replace("বিবরণ", "").replace("তথ্য", "")} {index + 1}</p><p className="mt-1 text-xs text-slate-500" data-id="element-1109" data-mp="3u8pddu0mpa3z">আবশ্যক তথ্য পূরণ করে দলিল যুক্ত করুন।</p></div>{records.length > 1 && <button onClick={() => onRemove(kind, record)} className="inline-flex items-center gap-1 text-xs font-bold text-red-700" data-id="element-1110" data-mp="32xoexyl83ir"><Trash2 className="h-4 w-4" data-id="element-1111" data-mp="c1jf5ge7qoum" />মুছুন</button>}</div><div className="grid gap-4 sm:grid-cols-2" data-id="element-1112" data-mp="c2b0lye3j676">{fields.map(field => <FormFieldView key={field.label} field={field} wide={field.type === "textarea"} data-id="element-1113" data-mp="h0vyvs9sffsz" />)}<UploadTile label={attachment} helper="PDF/JPG/PNG · সর্বোচ্চ ১০ MB" initial={index === 0 && kind === "deed" ? "waqf-deed-2011.pdf · 2.4 MB" : undefined} data-id="element-1114" data-mp="lzpg9vs73qyf" /></div></section>)}
    <Button variant="outline" onClick={() => onAdd(kind)} data-id="element-1115" data-mp="qr7e4ayx9bf6"><Plus className="mr-1.5 h-4 w-4" data-id="element-1116" data-mp="vps65n40sosj" />{addLabel}</Button>
  </div>;
}
function DocumentRow({
  document,
  onUpload
}: {
  document: UploadEntry;
  onUpload: () => void;
}) {
  const ready = document.state === "ready";
  return <div className={`flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${document.state === "error" ? "border-red-200 bg-red-50" : "border-slate-200"}`} data-id="element-1117" data-mp="14qynfyehu3mu">
    <div className="flex min-w-0 items-center gap-3" data-id="element-1118" data-mp="2k8d5tmaxonnc"><span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${ready ? "bg-emerald-50 text-[#006a4e]" : document.state === "error" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`} data-id="element-1119" data-mp="2g7jiau1elauz">{ready ? <CheckCircle2 className="h-5 w-5" data-id="element-1120" data-mp="37z8u49g6l0pu" /> : <FileText className="h-5 w-5" data-id="element-1121" data-mp="37y7io3v96c7l" />}</span><span className="min-w-0" data-id="element-1122" data-mp="33x41ne1gya4s"><b className="block text-sm" data-id="element-1123" data-mp="33wm0c2zy95q0">{document.name}{document.required && <span className="text-[#f42a41]" data-id="element-1124" data-mp="33zhx3o029ocv"> *</span>}</b><span className="block truncate text-xs text-slate-500" data-id="element-1125" data-mp="2z5dn0vvi107k">{ready ? document.fileName : document.state === "uploading" ? "ফাইল আপলোড হচ্ছে…" : "এখনও ফাইল যুক্ত করা হয়নি"}</span></span></div>
    <div className="flex shrink-0 items-center gap-2" data-id="element-1126" data-mp="3rmydptdyrunz">{document.state === "uploading" ? <Progress className="w-20" value={62} data-id="element-1127" data-mp="3mw9m8e2aaxou" /> : ready ? <><Button variant="outline" size="sm" data-id="element-1128" data-mp="3muosgqnsnis1"><Eye className="mr-1 h-4 w-4" data-id="element-1129" data-mp="9mudc53ghto0" />দেখুন</Button><StatusChip label="যুক্ত হয়েছে" tone="green" data-id="element-1130" data-mp="9o5jucoadfit" /></> : <Button variant="outline" size="sm" onClick={onUpload} data-id="element-1131" data-mp="elxkbv2zy9ym"><Upload className="mr-1 h-4 w-4" data-id="element-1132" data-mp="elfh2ehx2vh7" />ফাইল যুক্ত করুন</Button>}</div>
  </div>;
}
function UploadTile({
  label,
  helper,
  initial
}: {
  label: string;
  helper: string;
  initial?: string;
}) {
  const [state, setState] = useState<UploadState>(initial ? "ready" : "empty");
  const [fileName, setFileName] = useState(initial);
  function startUpload() {
    setState("uploading");
    window.setTimeout(() => {
      setFileName(`${label.replaceAll(" ", "-").toLowerCase()}.pdf · 1.2 MB`);
      setState("ready");
    }, 850);
  }
  return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-4" data-id="element-1133" data-mp="3i2nxctwzxn9t"><p className="text-sm font-bold" data-id="element-1134" data-mp="3i4qjv3yxwy9s">{label}</p><p className="mt-1 text-xs text-slate-500" data-id="element-1135" data-mp="1c963vkjft8xz">{helper}</p>{state === "uploading" ? <div className="mt-3" data-id="element-1136" data-mp="1gbara1jal9c6"><Progress value={64} data-id="element-1137" data-mp="1gbsx9pvrb1xa" /><p className="mt-1 text-xs text-slate-500" data-id="element-1138" data-mp="1g9zpu8dbkdo8">আপলোড হচ্ছে…</p></div> : state === "ready" ? <div className="mt-3 flex items-center justify-between gap-2" data-id="element-1139" data-mp="30sjwnhwdzysg"><span className="text-xs font-semibold text-[#006a4e]" data-id="element-1140" data-mp="2wrqbuxe8r03s"><CheckCircle2 className="mr-1 inline h-4 w-4" data-id="element-1141" data-mp="2wt1daqk7rvrq" />{fileName}</span><button aria-label="ফাইল মুছুন" onClick={() => {
        setState("empty");
        setFileName(undefined);
      }} className="text-slate-400 hover:text-red-700" data-id="element-1142" data-mp="3ajoecpdg5xqz"><X className="h-4 w-4" data-id="element-1143" data-mp="35qbq4gcojsyr" /></button></div> : <Button variant="outline" size="sm" className="mt-3" onClick={startUpload} data-id="element-1144" data-mp="3kgt0wqb7fv8b"><Upload className="mr-1 h-4 w-4" data-id="element-1145" data-mp="3fi828vblpjlz" />ফাইল যুক্ত করুন</Button>}</div>;
}
function FormFieldView({
  field,
  wide = false,
  error = false
}: {
  field: FormField;
  wide?: boolean;
  error?: boolean;
}) {
  const inputClass = `mt-2 ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`;
  return <label className={`block text-sm font-bold text-slate-700 ${wide ? "sm:col-span-2" : ""}`} data-id="element-1146" data-mp="1z3zrrz43v0qp">{field.label}{field.required !== false && <span className="text-[#f42a41]" data-id="element-1147" data-mp="1u5oibphq2s90"> *</span>}
    {field.type === "textarea" ? <Textarea className={inputClass} placeholder={field.placeholder || `${field.label} লিখুন`} defaultValue={field.defaultValue} data-id="element-1148" data-mp="1wjtn1p7o3693" /> : field.type === "select" ? <select className={`h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm ${inputClass}`} defaultValue={field.options?.[0]} data-id="element-1149" data-mp="20jkcnplgjgc1">{field.options?.map(option => <option key={option} data-id="element-1150" data-mp="2ajkxxh2w06iv">{option}</option>)}</select> : <Input className={`h-10 ${inputClass}`} type={field.type === "date" ? "date" : "text"} placeholder={field.placeholder || (field.type === "currency" ? "৳ ০" : `${field.label} লিখুন`)} defaultValue={field.defaultValue} data-id="element-1151" data-mp="2ah8e0ydkjm36" />}
    {error ? <span className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-red-700" data-id="element-1152" data-mp="1mt8h1gpnyzg5"><AlertCircle className="h-3.5 w-3.5" data-id="element-1153" data-mp="1rkgq2bn30iss" />এই তথ্যটি আবশ্যক।</span> : field.helper && <span className="mt-1 block text-xs font-normal text-slate-500" data-id="element-1154" data-mp="1rmjmbdetv85y">{field.helper}</span>}
  </label>;
}
function StepHeading({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="mb-6" data-id="element-1155" data-mp="z1tb7dyoi005"><h2 className="text-xl font-bold text-slate-900" data-id="element-1156" data-mp="rkd7oxsyohi1">{title}</h2><p className="mt-1 text-sm leading-6 text-slate-600" data-id="element-1157" data-mp="rjvc83e4i7b6">{description}</p></div>;
}
function SectionCard({
  title,
  description,
  children,
  className = ""
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <section className={`rounded-xl border border-slate-200 p-4 sm:p-5 ${className}`} data-id="element-1158" data-mp="cmci0liof1wc"><h3 className="font-bold" data-id="element-1159" data-mp="8marn8ie5uwd">{title}</h3><p className="mt-1 text-xs leading-5 text-slate-500" data-id="element-1160" data-mp="8nbzl444219v">{description}</p><div className="mt-5" data-id="element-1161" data-mp="3usme89c585l">{children}</div></section>;
}
function MoneyField({
  label,
  defaultValue
}: {
  label: string;
  defaultValue: string;
}) {
  return <label className="block text-sm font-bold" data-id="element-1162" data-mp="3uz0txadzhxng">{label}<div className="relative mt-2" data-id="element-1163" data-mp="3q3bsf1iem6oi"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500" data-id="element-1164" data-mp="3q43osejts8fu">৳</span><Input className="h-10 pl-8" inputMode="numeric" placeholder="০" defaultValue={defaultValue} data-id="element-1165" data-mp="3l2o2yk9tpxwe" /></div></label>;
}
function SummaryBox({
  label,
  value,
  tone = "slate"
}: {
  label: string;
  value: string;
  tone?: "slate" | "green";
}) {
  return <div className={`rounded-xl border p-4 ${tone === "green" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-id="element-1166" data-mp="3bfbs98q071v8"><p className="text-xs text-slate-500" data-id="element-1167" data-mp="2xgu63ffqxxff">{label}</p><p className="mt-1 text-lg font-bold" data-id="element-1168" data-mp="2xi5c7lwnzh9p">{value}</p></div>;
}
function ValidationNotice({
  message
}: {
  message: string;
}) {
  return <div className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900" data-id="element-1169" data-mp="3q3y7zbjjlvmz"><AlertCircle className="mt-0.5 h-5 w-5 shrink-0" data-id="element-1170" data-mp="3u6b9fnr9b7dy" /><div data-id="element-1171" data-mp="3u85vj5e9l5jm"><b data-id="element-1172" data-mp="3u85tkzupevgz">কিছু তথ্য প্রয়োজন</b><p className="mt-1" data-id="element-1173" data-mp="3gewq1ftft46d">{message}</p></div></div>;
}
function PreviewRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex justify-between gap-4 border-b border-slate-200 pb-3 last:border-0" data-id="element-1174" data-mp="3lcgfzczhz5e5"><span className="text-slate-500" data-id="element-1175" data-mp="cwwq8ny1c53u">{label}</span><b className="text-right" data-id="element-1176" data-mp="cvv33l3qvs5r">{value}</b></div>;
}